export class CachedXHR extends XMLHttpRequest {
  static cacheDisabled = true;

  public cachedResponse: Blob | null = null;
  private urlInternal: string = '';
  private expTime: number;

  constructor(expTime: number = 3600000) {
    super();
    this.expTime = expTime;
  }

  override open(
    method: string,
    url: string | URL,
    async: boolean = true,
    username?: string | null,
    password?: string | null,
  ): void {
    this.urlInternal = typeof url === 'string' ? url : url.toString();
    super.open(method, url, async, username ?? null, password ?? null);
  }

  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('xhr-cache-db', 1);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('files')) {
          db.createObjectStore('files', { keyPath: 'url' });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private async checkCache(): Promise<Blob | null> {
    const db = await this.openDB();
    await CachedXHR.cleanCachedFiles(db);

    return new Promise((resolve, reject) => {
      const tx = db.transaction('files', 'readonly');
      const store = tx.objectStore('files');
      const getReq = store.get(this.urlInternal);

      getReq.onsuccess = () => {
        const result = getReq.result;
        resolve(result ? (result.blob as Blob) : null);
      };

      getReq.onerror = () => reject(getReq.error);
    });
  }

  private async saveToCache(blob: Blob): Promise<void> {
    const db = await this.openDB();
    const tx = db.transaction('files', 'readwrite');
    const store = tx.objectStore('files');

    store.put({
      url: this.urlInternal,
      blob: blob,
      exp: Date.now() + this.expTime,
      cachedAt: Date.now(),
    });
  }

  override send(body?: XMLHttpRequestBodyInit | null | undefined): void {
    if (CachedXHR.cacheDisabled) {
      this.responseType = 'blob';

      this.addEventListener('load', () => {
        const blob = this.response as Blob;
        this.dispatchEvent(new CustomEvent('finish', { detail: blob }));
      });

      super.send(body);
      return;
    }

    this.checkCache().then((cachedBlob) => {
      if (cachedBlob) {
        console.log('Loaded from cache:', this.urlInternal);
        this.cachedResponse = cachedBlob;
        this.dispatchEvent(new CustomEvent('finish', { detail: cachedBlob }));
      } else {
        this.responseType = 'blob';

        this.addEventListener('load', () => {
          const blob = this.response as Blob;

          this.saveToCache(blob).catch((err) => {
            console.warn('Failed to write to cache:', err);
          });

          this.dispatchEvent(new CustomEvent('finish', { detail: blob }));
        });

        super.send(body);
      }
    });
  }

  static async cleanCachedFiles(db: IDBDatabase): Promise<void> {
    const tx = db.transaction('files', 'readonly');
    const store = tx.objectStore('files');

    const request = store.openCursor();

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;

      if (cursor) {
        const entry = cursor.value;

        if (entry.exp < Date.now()) {
          console.log(`Deleting expired cache entry: ${entry.url}`);
          const deleteTx = db.transaction('files', 'readwrite');
          const deleteStore = deleteTx.objectStore('files');
          deleteStore.delete(entry.url);
        }

        cursor.continue();
      }
    };

    request.onerror = () => {
      console.error('Error viewing cache.');
    };
  }
}
